import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { BreadcrumpComponent } from "../components/subcomponents/BreadcrumpCompont";
import { PageSectionHeader } from "../components/subcomponents/PageSectionHeader";
import { PageSkeleton } from "../components/PageSkeleton";
import { apiGet } from "../utils/api";
import { routes } from "../utils/utils.routes";
import { APPNAME } from "../utils/utils.constants";
import { useAcademicYear } from "../contexts/AcademicYearContext";
import bigCover from "../assets/images/big-cover.jpg";
import { FiPhone, FiCheckCircle } from "react-icons/fi";
import {
  parseGuideHtml, detectSectionKind, splitAmountLine,
  GuideSection, GuideGroup,
} from "../utils/parseGuide";
import "../styles/admission-guide.scss";

const I = (C: any) => C as any;
const PhoneI = I(FiPhone);
const CheckI = I(FiCheckCircle);

const SITE_URL = "https://ulpgl.net";
const TIER_COLORS = ["#041797", "#0d9488", "#ea580c", "#7c3aed", "#dc2626", "#0369a1"];

function useScrollSpy(ids: string[]) {
  const [activeId, setActiveId] = useState<string>(ids[0] || "");

  useEffect(() => {
    if (!ids.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-120px 0px -70% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ids]);

  return activeId;
}

const PriceCard: React.FC<{ group: GuideGroup; color: string }> = ({ group, color }) => (
  <div className="guide-price-card" style={{ ["--tier-color" as any]: color }}>
    <div className="guide-price-card__head">
      {group.eyebrow && <div className="guide-price-card__eyebrow">{group.eyebrow}</div>}
      <h4 className="guide-price-card__title">{group.title || "Détail"}</h4>
      {group.total && (
        <div className="guide-price-card__total">
          <b>{group.total}</b>
          <span>total à payer</span>
        </div>
      )}
    </div>
    {group.items.length > 0 && (
      <ul className="guide-price-card__items">
        {group.items.map((item, i) => {
          const split = splitAmountLine(item);
          return split ? (
            <li key={i}>
              <span>{split.label}</span>
              <b>{split.amount}</b>
            </li>
          ) : (
            <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
          );
        })}
      </ul>
    )}
  </div>
);

const ChecklistGrid: React.FC<{ groups: GuideGroup[] }> = ({ groups }) => {
  const items = groups.flatMap((g) => g.items);
  return (
    <div className="guide-checklist">
      {items.map((item, i) => (
        <div className="guide-checklist__item" key={i}>
          <CheckI className="guide-checklist__icon" size={18} />
          <span dangerouslySetInnerHTML={{ __html: item }} />
        </div>
      ))}
    </div>
  );
};

const InfoGrid: React.FC<{ groups: GuideGroup[] }> = ({ groups }) => (
  <div className="guide-info-grid">
    {groups.map((g, i) => (
      <div className="guide-info-card" key={i}>
        <h4 className="guide-info-card__title">{g.title || "Autres informations"}</h4>
        {g.introHtml && (
          <div className="guide-info-card__intro" dangerouslySetInnerHTML={{ __html: g.introHtml }} />
        )}
        <ul className="guide-info-card__list">
          {g.items.map((item, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ul>
      </div>
    ))}
  </div>
);

const GuideSectionBlock: React.FC<{ section: GuideSection }> = ({ section }) => {
  const kind = detectSectionKind(section);

  return (
    <section id={section.id} className="guide-section">
      <div className="guide-section__header">
        <h2 className="guide-section__title">{section.title}</h2>
        {section.introHtml && (
          <div className="guide-section__intro" dangerouslySetInnerHTML={{ __html: section.introHtml }} />
        )}
      </div>

      {kind === "pricing" && (
        <div className="guide-pricing-grid">
          {section.groups.map((g, i) => (
            <PriceCard key={i} group={g} color={TIER_COLORS[i % TIER_COLORS.length]} />
          ))}
        </div>
      )}
      {kind === "checklist" && <ChecklistGrid groups={section.groups} />}
      {kind === "grid" && <InfoGrid groups={section.groups} />}
    </section>
  );
};

const GuideNav: React.FC<{ sections: GuideSection[]; activeId: string }> = ({ sections, activeId }) => (
  <nav className="guide-nav" aria-label="Sommaire du guide">
    <ul className="guide-nav__list">
      {sections.map((s, i) => (
        <li key={s.id} className={`guide-nav__item${activeId === s.id ? " is-active" : ""}`}>
          <a href={`#${s.id}`}>
            <span className="guide-nav__num">{i + 1}</span>
            {s.title.replace(/^\d+\.\s*/, "")}
          </a>
        </li>
      ))}
    </ul>
  </nav>
);

const GuideHelpCard: React.FC<{ phones: string[]; variant: "desktop" | "mobile" }> = ({ phones, variant }) => {
  if (phones.length === 0) return null;
  return (
    <div className={`guide-help-card guide-help-card--${variant}`}>
      <h4>Besoin d&apos;aide ?</h4>
      <p>Le Secrétariat Général Académique répond à vos questions sur les inscriptions.</p>
      {phones.map((phone) => (
        <a key={phone} className="guide-help-card__phone" href={`tel:${phone.replace(/\s+/g, "")}`}>
          <PhoneI size={14} /> {phone}
        </a>
      ))}
      <Link className="guide-help-card__link" to={routes.CONTACTS}>
        Nous contacter →
      </Link>
    </div>
  );
};

export const AdmissionGuide: React.FC = () => {
  const { academicYear: ctxYear, setAcademicYear } = useAcademicYear();
  const [item, setItem] = useState<any | null>(undefined as any);

  useEffect(() => {
    apiGet("/admission-guide")
      .then((d) => {
        const it = d.item || null;
        setItem(it);
        if (it?.academicYear?.trim()) setAcademicYear(it.academicYear.trim());
      })
      .catch(() => setItem(null));
  }, [setAcademicYear]);

  const sections = useMemo(() => parseGuideHtml(item?.contentHtml || ""), [item?.contentHtml]);
  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections]);
  const activeId = useScrollSpy(sectionIds);

  if (item === undefined) return <PageSkeleton />;

  const title = item?.title || "Guide d'inscription";
  const academicYear = item?.academicYear?.trim() || ctxYear;
  const phones = (item?.contactPhones || "").split(",").map((p: string) => p.trim()).filter(Boolean);
  const hasContent = sections.length > 0;

  return (
    <>
      <Helmet>
        <title>{title} | {APPNAME}</title>
        <meta
          name="description"
          content={`Frais d'inscription, documents à fournir et informations nécessaires pour s'inscrire à ${APPNAME}${academicYear ? ` (${academicYear})` : ""}.`}
        />
        <meta property="og:title" content={title} />
        <meta property="og:url" content={`${SITE_URL}${routes.GUIDE_INSCRIPTION}`} />
        <meta property="og:type" content="article" />
      </Helmet>

      <BreadcrumpComponent
        title={title}
        subtitle={
          academicYear
            ? `Frais, documents et informations nécessaires pour votre inscription — Année académique ${academicYear}.`
            : "Frais, documents et informations nécessaires pour votre inscription."
        }
        imageCover={bigCover}
      />

      <div className="padding-x site-content guide-page">
        <main className="wp-block-group alignfull has-global-padding is-layout-constrained wp-block-group-is-layout-constrained">
          {!hasContent ? (
            <div className="guide-fallback">
              <PageSectionHeader
                eyebrow="Inscription"
                title="Guide d'inscription à venir"
                lead="Ce contenu n'a pas encore été publié. Contactez le Secrétariat Général Académique pour toute question relative aux inscriptions."
                align="center"
              />
              <Link
                to={routes.CONTACTS}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  marginTop: 8, padding: "10px 24px", borderRadius: 8,
                  background: "#041797", color: "#fff",
                  fontSize: 14, fontWeight: 600, textDecoration: "none",
                }}
              >
                Nous contacter
              </Link>
            </div>
          ) : (
            <div className="guide-layout">
              <div className="guide-sidebar">
                <GuideNav sections={sections} activeId={activeId} />
                <GuideHelpCard phones={phones} variant="desktop" />
              </div>
              <div className="guide-content">
                {item.introHtml && (
                  <div
                    className="rich-content guide-lead"
                    dangerouslySetInnerHTML={{ __html: item.introHtml }}
                  />
                )}
                {sections.map((s) => (
                  <GuideSectionBlock key={s.id} section={s} />
                ))}
                <GuideHelpCard phones={phones} variant="mobile" />
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};
